import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

interface ImpersonationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImpersonate: (netId: string) => void;
}

interface ValidatedUser {
  name: string;
  netId: string;
  roles: string;
}

const ImpersonationDialog: React.FC<ImpersonationDialogProps> = ({ isOpen, onClose, onImpersonate }) => {
  const [netId, setNetId] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [validatedUser, setValidatedUser] = useState<ValidatedUser | null>(null);
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const baseUrl = process.env.REACT_APP_API_URL;

  const validateNetId = async (): Promise<void> => {
    setIsValidating(true);
    setError('');
    try {
      const response = await fetch(`${baseUrl}/api/auth/validatenetid`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ netId })
      });

      if (!response.ok) {
        throw new Error('Invalid NetID');
      }

      const userData = await response.json();
      if(!userData.name) {
        throw new Error('Invalid NetID');
      }
      setValidatedUser({
        name: userData.name,
        netId: userData.netId,
        roles: userData.roles.Roles.join(", ")
      });
    } catch (err) {
      setError('Invalid NetID');
      setValidatedUser(null);
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!netId.trim()) {
      setError('Please enter a NetID');
      return;
    }
    await validateNetId();
  };

  const handleGoClick = (): void => {
    if (validatedUser) {
      onImpersonate(netId);
      setNetId('');
      setValidatedUser(null);
    }
  };

  const handleGoBack = (): void => {
    setValidatedUser(null);
    setError('');
  };

  const handleCancel = (): void => {
    setNetId('');
    setError('');
    setValidatedUser(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      {isValidating && (
        <div className="fixed inset-0 bg-white bg-opacity-30 backdrop-blur-sm z-[60] pointer-events-none">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center pointer-events-auto">
            <FontAwesomeIcon 
              icon={faSpinner} 
              className="text-blue-500 text-4xl animate-spin mb-4" 
            />
            <div className="bg-white bg-opacity-90 px-6 py-3 rounded-lg shadow-lg">
              <p className="text-gray-700 text-lg">Validating NetID...</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4">
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <h2 className="text-xl font-semibold text-gray-800">Impersonate User</h2>
          <button
            onClick={handleCancel}
            className="text-gray-500 hover:text-gray-700 text-2xl w-8 h-8 flex items-center justify-center"
          >
            ×
          </button>
        </div>

        <div className="p-6">
          {!validatedUser ? (
            <form onSubmit={handleSubmit}>
              <div className="mb-4 border-b">
                <input
                  type="text"
                  value={netId}
                  onChange={(e) => setNetId(e.target.value)}
                  placeholder="Enter NetID"
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    error ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {error && (
                  <p className="mt-1 text-red-500 text-sm">{error}</p>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 text-white bg-gray-500 rounded-md hover:bg-gray-600 focus:outline-none"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isValidating}
                  className="px-4 py-2 text-white bg-blue-500 rounded-md hover:bg-blue-600 focus:outline-none disabled:opacity-50"
                >
                  GO
                </button>
              </div>
            </form>
          ) : (
            <div>
              <div className="space-y-4 mb-6">
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-gray-600 font-medium">Name</div>
                  <div>{validatedUser.name}</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-gray-600 font-medium">NetID</div>
                  <div>{validatedUser.netId}</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-gray-600 font-medium">Roles</div>
                  <div>{validatedUser.roles}</div>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleGoBack}
                  className="px-4 py-2 text-white bg-yellow-500 rounded-md hover:bg-yellow-600 focus:outline-none"
                >
                  GO BACK
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 text-white bg-gray-500 rounded-md hover:bg-gray-600 focus:outline-none"
                >
                  CANCEL
                </button>
                <button
                  type="button"
                  onClick={handleGoClick}
                  className="px-4 py-2 text-white bg-blue-500 rounded-md hover:bg-blue-600 focus:outline-none"
                >
                  GO
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImpersonationDialog;